import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LoadingSpinner, EmptyState } from '../../../components/layout/SharedComponents';
import api from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { useExpenditureLabels, useDecimalFormat } from '../../../contexts/ExpenditureLabelsContext';
import DataLookupField from '../../../components/DataLookupField';
import {
  Plus, Edit, Trash2, Save, Search, Download, X,
  FileText, Users, ChevronRight, ChevronDown, Bell,
  Lock, Eye, Pencil, Settings, Package
} from 'lucide-react';

export function PurchasingManagerPanel() {
  const [settings, setSettings] = useState(null);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/api/admin/purchasing-manager');
      setSettings(res.data.settings || {});
      setEligibleUsers(res.data.eligible_users || []);
    } catch (err) {
      if (err?.response?.status >= 500) toast.error('Failed to load settings');
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/admin/purchasing-manager', settings);
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold mb-1">User Maintenance</h1>
      <p className="text-lg font-medium text-muted-foreground mb-6">Purchasing Manager</p>
      
      <div className="border-t pt-4 mb-6">
        <p className="text-sm text-muted-foreground mb-6">
          For an order to be valid, your internal business rules may insist it bear the signature of a Purchasing Manager. 
          To ensure compliance when electronically publishing your orders, you can select one Senior Buyer as the 
          Purchasing Manager and display their name, job title and an image of their signature on every order.
        </p>
      </div>

      {/* Purchasing Manager Selection */}
      <div className="space-y-6">
        <div className="flex items-center gap-8">
          <Label className="w-48 text-sm">Purchasing Manager</Label>
          <select
            value={settings.purchasing_manager_user_id || ''}
            onChange={(e) => setSettings(p => ({ ...p, purchasing_manager_user_id: e.target.value }))}
            className="w-64 h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="">-- Select --</option>
            {eligibleUsers.map(u => (
              <option key={u.user_id} value={u.user_id}>{u.name}</option>
            ))}
          </select>
        </div>

        {/* Order Signatory */}
        <div className="flex items-start gap-8 border-t pt-4">
          <Label className="w-48 text-sm pt-2">Select Order Signatory</Label>
          <div className="space-y-2">
            {[
              { value: 'purchasing_manager', label: 'Purchasing Manager' },
              { value: 'order_buyer', label: 'Order Buyer' },
              { value: 'last_cost_centre_approver', label: 'Last Cost Centre Approver' }
            ].map(opt => (
              <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="order_signatory"
                  value={opt.value}
                  checked={settings.order_signatory === opt.value}
                  onChange={(e) => setSettings(p => ({ ...p, order_signatory: e.target.value }))}
                  className="h-4 w-4 text-primary"
                />
                <span className="text-sm">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Signature Options */}
        <div className="space-y-3 border-t pt-4">
          <div className="flex items-center gap-3">
            <Switch
              checked={settings.show_signatory_name}
              onCheckedChange={(v) => setSettings(p => ({ ...p, show_signatory_name: v }))}
            />
            <span className="text-sm">Signatory name on footer of Purchase Order</span>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              checked={settings.show_signatory_signature}
              onCheckedChange={(v) => setSettings(p => ({ ...p, show_signatory_signature: v }))}
            />
            <span className="text-sm">Signatory signature on footer of Purchase Order</span>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              checked={settings.show_signature_line}
              onCheckedChange={(v) => setSettings(p => ({ ...p, show_signature_line: v }))}
            />
            <span className="text-sm">Show 'Signature' text and dotted line on Purchase Order e.g (Signature ..............)</span>
          </div>
        </div>

        {/* Request Ownership */}
        <div className="flex items-start gap-8 border-t pt-4">
          <Label className="w-48 text-sm pt-1">Request Ownership</Label>
          <div className="flex items-center gap-3">
            <Switch
              checked={settings.allow_pm_assign_buyers}
              onCheckedChange={(v) => setSettings(p => ({ ...p, allow_pm_assign_buyers: v }))}
            />
            <span className="text-sm">Allow Purchasing Manager to actively assign buyers to Requests instead of Buyers Taking Ownership</span>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end mt-8 border-t pt-4">
        <Button onClick={handleSave} disabled={saving}>
          <Save className="h-4 w-4 mr-1" />
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
